import clsx from "clsx";
import { TimelineInterface } from "../types/chase-timeline-types";
import moment from "moment";
import { useState } from "react";
import chaseTimeLineData from "../utils/data/chase-timeline-data";

/** Days from here on are the final run-in and are called out in amber. */
const FINAL_STRETCH_DAY = 29;

/**
 * Chase timeline.
 *
 * A rounded rail with one dot per day: the completed run is a gradient fill, the
 * final stretch carries an amber wash behind its dots, and each dot is its own hover
 * target. Labels are shown for the first, last and current step plus whatever is
 * hovered — labelling every dot produces a wall of overlapping text.
 */
const ProgressBar = ({ timeline }: { timeline?: TimelineInterface[] }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  const steps = [...(timeline ?? [])].sort((a, b) => a?.day - b?.day);
  if (!steps.length) return null;

  const currentStep = [...steps]
    .sort((a, b) => b?.day - a?.day)
    .find((step) => step?.done !== false);

  const currentDay = currentStep?.day ?? 0;
  const lastIndex = steps.length - 1;

  // The current step always keeps its label. Suppress the first/last bookend label
  // when the current step is close enough that the two cards would collide —
  // overlapping labels are worse than one fewer date on screen.
  const COLLISION_STEPS = 4;
  const crowdsStart = Math.abs(currentDay - steps[0].day) < COLLISION_STEPS;
  const crowdsEnd = Math.abs(steps[lastIndex].day - currentDay) < COLLISION_STEPS;

  const firstFinalIndex = steps.findIndex((s) => s.day >= FINAL_STRETCH_DAY);
  const amberWidth =
    firstFinalIndex >= 0 ? 100 - (100 * firstFinalIndex) / lastIndex : 0;

  return (
    <div className="pt-6 pb-12 lg:pb-2 max-sm:overflow-x-auto max-sm:px-8">
      <div className="w-full max-sm:min-w-[520px] h-2 rounded-full bg-overlay-mid border border-edge flex items-center justify-between relative shadow-inner">
        {/* Completed run. */}
        <div
          className="absolute left-0 top-[50%] translate-y-[-50%] h-1 bg-gradient-to-r from-accent to-blue max-w-[100%] rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(100, Math.max(0, (100 * (currentDay - 1)) / lastIndex))}%`,
          }}
        />

        {/* Final run-in, washed behind the amber dots so the deadline stretch reads
            as a region rather than as a few isolated dots. */}
        {amberWidth > 0 ? (
          <div
            className="w-full h-full bg-amber-500/20 absolute right-0 rounded-full"
            style={{ width: `${amberWidth}%` }}
          />
        ) : null}

        {steps.map((item, index) => {
          const stepDate = new Date(item?.startDate);
          stepDate.setDate(stepDate.getDate() + index);

          const isDone = item.day <= currentDay;
          const isCurrent = item.day === currentDay;
          const isFinal = item.day >= FINAL_STRETCH_DAY;
          const isHovered = hovered === item.day;

          const knownDay = chaseTimeLineData.find((d) => d.day === item.day);
          const showLabel =
            isHovered ||
            (hovered === null &&
              (isCurrent ||
                (index === 0 && !crowdsStart) ||
                (index === lastIndex && !crowdsEnd)));

          return (
            <div
              key={index}
              onMouseEnter={() => setHovered(item.day)}
              onMouseLeave={() => setHovered(null)}
              className={clsx(
                "h-[14px] w-[14px] bg-surface-elevated border border-edge-strong rounded-full flex items-center justify-center cursor-pointer relative transition-transform duration-200 hover:scale-110",
                isCurrent && "!h-[20px] !w-[20px]"
              )}
            >
              <div
                className={clsx(
                  "relative w-[80%] h-[80%] rounded-full bg-overlay-mid",
                  isDone && "!bg-gradient-to-br !from-accent !to-blue",
                  isFinal && "!bg-amber-400"
                )}
              />

              {showLabel && (knownDay || index === 0 || index === lastIndex) ? (
                <div
                  className={clsx(
                    "absolute top-[120%] left-[50%] translate-x-[-50%] text-center rounded-lg bg-surface-elevated border border-edge px-2 py-1 shadow-card z-[20]",
                    // Bookends pull in so they never overflow the rail's ends.
                    index === 0 && "!left-[-1px] !translate-x-0 !text-start",
                    index === lastIndex &&
                      "!left-[100%] !translate-x-[-100%] !text-right"
                  )}
                >
                  <h3 className="text-[.8rem] font-medium text-heading-color m-0 p-0 !text-nowrap">
                    Day {item.day}
                    {isFinal ? " 🔔" : ""}
                  </h3>
                  <p className="text-[.7rem] text-light-gray font-semibold !text-nowrap">
                    {moment(stepDate).format("DD-MM-YYYY")}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressBar;
